import { useAllMarkets } from 'client/hooks/query/markets/allMarkets/useAllMarkets';
import { MarginMode } from 'client/modules/localstorage/userSettings/types/tradingSettings';
import { useSavedUserSettings } from 'client/modules/localstorage/userSettings/useSavedUserSettings';
import { getDefaultPerpLeverage } from 'client/pages/PerpTrading/utils/defaultPerpLeverage';
import { useCallback, useMemo } from 'react';

export function coerceMarginModeForMarket(
  marginMode: MarginMode,
  isIsolatedOnlyMarket: boolean,
): MarginMode {
  if (!isIsolatedOnlyMarket) {
    return marginMode;
  }

  return {
    mode: 'isolated',
    leverage: marginMode.leverage,
    enableBorrows:
      marginMode.mode === 'isolated' ? marginMode.enableBorrows : true,
  };
}

export function useSelectedPerpMarginMode(productId: number | undefined) {
  const { savedUserSettings, setSavedUserSettings } = useSavedUserSettings();
  const { data: allMarketsData } = useAllMarkets();
  const leverageByProductId = savedUserSettings.trading.leverageByProductId;
  const marginModeSettings = savedUserSettings.trading.marginMode;

  const market = productId ? allMarketsData?.perpMarkets[productId] : undefined;
  const maxLeverage = market?.maxLeverage ?? 1;
  const isIsolatedOnlyMarket = market?.isolatedOnly === true;

  /**
   * Legacy cross-margin settings
   */

  const persistedCrossMarginLeverageSelection = productId
    ? leverageByProductId[productId]
    : undefined;

  const selectedCrossMarginLeverage = useMemo(() => {
    if (!persistedCrossMarginLeverageSelection) {
      return getDefaultPerpLeverage(maxLeverage);
    }

    return Math.min(maxLeverage, persistedCrossMarginLeverageSelection);
  }, [maxLeverage, persistedCrossMarginLeverageSelection]);

  const setSelectedCrossMarginLeverage = useCallback(
    (selectedValue: number) => {
      if (!productId) {
        return;
      }

      setSavedUserSettings((prev) => {
        prev.trading.leverageByProductId[productId] = Math.min(
          maxLeverage,
          selectedValue,
        );
        return prev;
      });
    },
    [maxLeverage, productId, setSavedUserSettings],
  );

  /**
   * New Iso / Cross margin settings
   */
  const savedMarginModeForProduct = productId
    ? marginModeSettings.lastSelected[productId]
    : undefined;

  const savedMarginMode = useMemo((): MarginMode => {
    if (!savedMarginModeForProduct) {
      switch (marginModeSettings.default) {
        case 'isolated':
          return {
            mode: 'isolated',
            leverage: getDefaultPerpLeverage(maxLeverage),
            enableBorrows: true,
          };
        case 'cross':
          return {
            mode: 'cross',
            leverage: getDefaultPerpLeverage(maxLeverage),
          };
      }
    }

    // Perform a sanity check on the saved leverage selection
    return {
      ...savedMarginModeForProduct,
      leverage: Math.min(maxLeverage, savedMarginModeForProduct.leverage),
    };
  }, [marginModeSettings.default, maxLeverage, savedMarginModeForProduct]);

  const selectedMarginMode = useMemo(
    () => coerceMarginModeForMarket(savedMarginMode, isIsolatedOnlyMarket),
    [isIsolatedOnlyMarket, savedMarginMode],
  );

  const setSelectedMarginMode = useCallback(
    (newValue: MarginMode) => {
      if (!productId) {
        return;
      }

      setSavedUserSettings((prev) => {
        const clampedValue: MarginMode = {
          ...newValue,
          leverage: Math.min(maxLeverage, newValue.leverage),
        };
        prev.trading.marginMode.lastSelected[productId] =
          coerceMarginModeForMarket(clampedValue, isIsolatedOnlyMarket);
        return prev;
      });
    },
    [isIsolatedOnlyMarket, maxLeverage, productId, setSavedUserSettings],
  );

  return {
    selectedCrossMarginLeverage,
    setSelectedCrossMarginLeverage,
    savedMarginMode,
    selectedMarginMode,
    isIsolatedOnlyMarket,
    setSelectedMarginMode,
  };
}
